/**
 * The budget, the last card of the Team settings sheet: the ONE budget a
 * ticket gets (decision 6, 2026-10-19), a dollar ceiling and a time ceiling
 * that planning, the build and its fix rounds share, plus how many turns the
 * Team starts on its own each day. A ticket that reaches its budget stops and
 * asks to be given more; this is where a person gives it more.
 *
 * The values are drafts like the rest of the sheet, and Save writes only what
 * changed through the Team's charter (`runLimits.ts` does the units). Whoever
 * can manage the Team changes them like every other field (the Team's owner,
 * a workspace owner or admin, the operator in God Mode with Act on); anyone
 * else reads the values without the controls.
 */
import { useId, type ReactNode } from "react";
import {
  DEFAULT_DAILY_TURN_BUDGET,
  DEFAULT_TICKET_BUDGET,
  MAX_DAILY_TURN_BUDGET,
} from "@yagni/shared";

import { Btn } from "../../queue/index.js";
import { LIMITS_READ_ONLY_NOTE } from "../copy.js";
import type { SettingsCtx } from "./context.js";
import { AgentCard, Field, Sect } from "./fields.js";
import {
  MAX_BUDGET_HOURS,
  MIN_BUDGET_HOURS,
  defaultRunLimitsDraft,
  fieldProblem,
  formatDollars,
  type RunLimitsDraft,
} from "./runLimits.js";

const DEFAULT_HOURS = DEFAULT_TICKET_BUDGET.maxElapsedMs / 3_600_000;

export function RunLimitsSection({ ctx }: { ctx: SettingsCtx }) {
  const { page } = ctx;
  const limits = ctx.team.limits;
  const disabled = ctx.readOnly || ctx.busy;
  // Read-only because the viewer cannot manage the Team, not because it is archived.
  const notTheirs = ctx.readOnly && page.team?.archivedAt == null;
  const usage = page.team?.automaticTurnUsage ?? null;

  const set = (update: (draft: RunLimitsDraft) => RunLimitsDraft) =>
    ctx.setTeam((team) => ({ ...team, limits: { ...update(team.limits), reset: false } }));

  return (
    <AgentCard
      id="limits"
      mark={<span className="tdot" aria-hidden />}
      name="Budget"
      role="How far one ticket may go"
      sub="Planning, the build and its fix rounds share one budget. When a ticket reaches it, it stops and asks you to give it more."
    >
      <Sect>Each ticket</Sect>
      <LimitField
        label="Budget"
        accessibleLabel="Ticket budget"
        unit="dollars"
        prefix="$"
        value={limits.dollars}
        step={0.01}
        min={0.01}
        disabled={disabled}
        problem={disabled ? null : fieldProblem("dollars", limits.dollars)}
        help={`Model and sandbox spend for the whole ticket. Default $${formatDollars(DEFAULT_TICKET_BUDGET.maxCostMillicents)}.`}
        testId="settings-limits-dollars"
        onChange={(dollars) => set((draft) => ({ ...draft, dollars }))}
      />
      <LimitField
        label="Time"
        accessibleLabel="Ticket time"
        unit="hours"
        value={limits.hours}
        step={1}
        min={MIN_BUDGET_HOURS}
        max={MAX_BUDGET_HOURS}
        disabled={disabled}
        problem={disabled ? null : fieldProblem("hours", limits.hours)}
        help={`Time its runs spend working, added up. Waiting on you does not count. From ${MIN_BUDGET_HOURS} to ${MAX_BUDGET_HOURS} hours. Default ${DEFAULT_HOURS}.`}
        testId="settings-limits-hours"
        onChange={(hours) => set((draft) => ({ ...draft, hours }))}
      />
      <Sect>Automatic work · each day</Sect>
      <LimitField
        label="Automatic turns"
        accessibleLabel="Automatic turns per day"
        unit="turns"
        value={limits.turns}
        step={1}
        min={1}
        max={MAX_DAILY_TURN_BUDGET}
        disabled={disabled}
        problem={disabled ? null : fieldProblem("turns", limits.turns)}
        help={
          <>
            Turns the Team starts on its own when its sources change. Turns you start by hand do not
            count. Default {DEFAULT_DAILY_TURN_BUDGET}.
            {usage && (
              <>
                {" "}
                <span data-testid="settings-limits-turn-usage">
                  {usage.used.toLocaleString("en-US")} used today.
                </span>
              </>
            )}
          </>
        }
        testId="settings-limits-turns"
        onChange={(turns) => set((draft) => ({ ...draft, turns }))}
      />
      {notTheirs ? (
        <p className="fact" data-testid="settings-limits-readonly">
          {LIMITS_READ_ONLY_NOTE}
        </p>
      ) : (
        !ctx.readOnly && (
          <div className="acts">
            <Btn
              variant="quiet"
              size="sm"
              disabled={ctx.busy}
              data-testid="settings-limits-reset"
              onClick={() => ctx.setTeam((team) => ({ ...team, limits: defaultRunLimitsDraft() }))}
            >
              Reset to defaults
            </Btn>
            {limits.reset && (
              <span className="fact">Save puts every limit back to its default.</span>
            )}
          </div>
        )
      )}
    </AgentCard>
  );
}

function LimitField({
  label,
  accessibleLabel,
  unit,
  prefix,
  value,
  onChange,
  step,
  min,
  max,
  disabled,
  problem,
  help,
  testId,
}: {
  label: string;
  accessibleLabel: string;
  unit: string;
  prefix?: string;
  value: string;
  onChange: (value: string) => void;
  step: number;
  min: number;
  max?: number;
  disabled: boolean;
  problem: string | null;
  help: ReactNode;
  testId: string;
}) {
  const id = useId();
  return (
    <Field
      label={label}
      htmlFor={id}
      help={problem ? <span style={{ color: "var(--incident)" }}>{problem}</span> : help}
    >
      <div className="in mono" style={{ maxWidth: 200, gap: 6 }}>
        {prefix && <span aria-hidden>{prefix}</span>}
        <input
          id={id}
          type="number"
          inputMode="decimal"
          aria-label={accessibleLabel}
          value={value}
          step={step}
          min={min}
          max={max}
          disabled={disabled}
          aria-invalid={problem ? true : undefined}
          data-testid={testId}
          onChange={(event) => onChange(event.target.value)}
        />
        <span className="fact" aria-hidden style={{ flex: "none" }}>
          {unit}
        </span>
      </div>
    </Field>
  );
}
